import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Eye, Target, Award, Users } from 'lucide-react';

export default function About() {
  const { t, isArabic } = useLanguage();

  const values = [
    {
      icon: Eye,
      titleKey: 'visionTitle',
      descKey: 'visionDesc',
    },
    {
      icon: Target,
      titleKey: 'missionTitle',
      descKey: 'missionDesc',
    },
    {
      icon: Award,
      titleKey: 'experienceTitle',
      descKey: 'experienceDesc',
    },
  ];

  const stats = [
    { value: '500+', labelAr: 'عميل راضٍ', labelEn: 'Happy Clients' },
    { value: '1000+', labelAr: 'منتج متوفر', labelEn: 'Products Available' },
    { value: '24/7', labelAr: 'دعم فني', labelEn: 'Technical Support' },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden" data-testid="about-hero">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-100 to-white" />
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `linear-gradient(to right, #cbd5e1 1px, transparent 1px), linear-gradient(to bottom, #cbd5e1 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          />
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 bg-black text-white text-xs font-medium rounded-full mb-6 tracking-wider uppercase">
              {t('about')}
            </span>
            <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 ${isArabic ? 'font-arabic' : 'font-english'}`}>
              {t('aboutTitle')}
            </h1>
            <p className="text-lg md:text-xl text-slate-600">
              {t('aboutSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="about-story">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: isArabic ? 30 : -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={isArabic ? 'lg:order-2' : 'lg:order-1'}
            >
              <div className="relative">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=800&q=80"
                    alt="Medical Team"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Decorative element */}
                <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-slate-100 rounded-2xl -z-10" />
                <div className="absolute -top-6 -right-6 w-32 h-32 bg-black rounded-2xl -z-10" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: isArabic ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={isArabic ? 'lg:order-1' : 'lg:order-2'}
            >
              <span className="text-xs uppercase tracking-widest text-slate-500 mb-4 block">
                {isArabic ? 'قصتنا' : 'Our Story'}
              </span>
              <h2 className={`text-3xl md:text-4xl font-bold text-slate-900 mb-6 ${isArabic ? 'font-arabic' : 'font-english'}`}>
                {isArabic ? 'البصمة الحديثة لاستيراد المعدات الطبية' : 'ALBASMA ALHADITHA'}
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                {t('aboutIntro')}
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-2xl font-bold text-black">500+</span>
                  <span className="text-slate-600 block text-sm">
                    {isArabic ? 'شريك وعميل' : 'Partners & Clients'}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-black text-white" data-testid="about-stats">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="grid grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <span className="text-4xl md:text-5xl font-bold text-white block mb-2">
                  {stat.value}
                </span>
                <span className="text-slate-400 text-sm">
                  {isArabic ? stat.labelAr : stat.labelEn}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="about-values">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-16"
          >
            <span className="text-xs uppercase tracking-widest text-slate-500 mb-4 block">
              {isArabic ? 'قيمنا' : 'Our Values'}
            </span>
            <h2 className={`text-3xl md:text-4xl font-bold text-slate-900 ${isArabic ? 'font-arabic' : 'font-english'}`}>
              {isArabic ? 'ما يميزنا' : 'What Sets Us Apart'}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow duration-300"
                data-testid={`value-card-${index}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-black flex items-center justify-center mb-6">
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className={`text-xl font-semibold text-slate-900 mb-4 ${isArabic ? 'font-arabic' : 'font-english'}`}>
                  {t(value.titleKey)}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {t(value.descKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team/Partners Image Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="about-partners">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className={`text-3xl md:text-4xl font-bold text-slate-900 mb-4 ${isArabic ? 'font-arabic' : 'font-english'}`}>
              {isArabic ? 'نعمل مع الجميع' : 'We Work With Everyone'}
            </h2>
            <p className="text-slate-600 text-lg">
              {isArabic 
                ? 'المستشفيات والعيادات والصيدليات والمراكز الطبية'
                : 'Hospitals, Clinics, Pharmacies, and Medical Centers'
              }
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=400&q=80',
              'https://images.unsplash.com/photo-1538108149393-fbbd81895907?auto=format&fit=crop&w=400&q=80',
              'https://images.unsplash.com/photo-1584982751601-97dcc096659c?auto=format&fit=crop&w=400&q=80',
              'https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=400&q=80',
            ].map((img, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="aspect-square rounded-xl overflow-hidden"
              >
                <img
                  src={img}
                  alt={`Medical ${index + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
